import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Sort a list of device objects alphabetically by display name, case-insensitive
 * and numeric-aware (e.g. "Device 2" sorts before "Device 10"). Used to keep
 * "All Devices" dropdowns easy to scan. Returns a new array; input is not mutated.
 * @param {Array} devices
 * @param {(device: object) => string} [getLabel] - resolves the display label for a device
 * @returns {Array}
 */
export function sortDevicesByName(devices, getLabel = (d) =>
  d?.hostname || d?.device_name || d?.computer_name || d?.device_uuid || d?.device_id || ''
) {
  if (!Array.isArray(devices)) return devices;
  return [...devices].sort((a, b) =>
    String(getLabel(a)).localeCompare(String(getLabel(b)), undefined, { sensitivity: 'base', numeric: true })
  );
}

/**
 * Resolve the effective timezone for a dashboard's date filters.
 * Priority: first selected team's TZ > selected device's team TZ > org TZ > UTC.
 * Teams carry `timezone` from /api/agent/devices/filter-list (team override >
 * shift template TZ), so "today" resolves to the team's own calendar day.
 * @param {object} args
 * @param {string|string[]} [args.selectedTeam] - team id(s), or 'all'
 * @param {string|string[]} [args.selectedDevice] - device id(s), or 'all'
 * @param {Array} [args.teams] - team objects carrying `timezone`
 * @param {Array} [args.devices] - device objects carrying `team_id`
 * @param {string} [args.orgTimezone] - org/tenant fallback timezone
 * @returns {string} IANA timezone name
 */
export function resolveFilterTimezone({ selectedTeam, selectedDevice, teams = [], devices = [], orgTimezone } = {}) {
  let tz = orgTimezone || 'UTC';
  const teamIds = Array.isArray(selectedTeam)
    ? selectedTeam.filter(Boolean)
    : (selectedTeam && selectedTeam !== 'all' ? [selectedTeam] : []);

  if (teamIds.length >= 1) {
    for (const teamId of teamIds) {
      const team = teams.find(t => (t?.team_id || t?.id) === teamId);
      if (team?.timezone) {
        tz = team.timezone;
        break;
      }
    }
  } else if (selectedDevice && selectedDevice !== 'all') {
    const deviceIds = Array.isArray(selectedDevice) ? selectedDevice.filter(Boolean) : [selectedDevice];
    const deviceObj = devices.find(d => (d?.device_uuid || d?.device_id || d?.id) === deviceIds[0]);
    if (deviceObj?.team_id) {
      const deviceTeam = teams.find(t => (t?.team_id || t?.id) === deviceObj.team_id);
      if (deviceTeam?.timezone) tz = deviceTeam.timezone;
    }
  }
  return tz;
}

// Inclusive calendar-day spans for the range tokens used by the Application
// Monitoring and Productivity dashboards. 'today' and '1d' are aliases.
const FILTER_RANGE_DAYS = {
  today: 1, '1d': 1, '3d': 3, '7d': 7, '14d': 14, '30d': 30, '90d': 90, '365d': 365,
};

/**
 * Build a { start_date, end_date } pair of YYYY-MM-DD calendar days for a range
 * token, evaluated in the supplied timezone rather than the browser's.
 * A custom range is emitted as the literal dates the user picked — shifting
 * those by timezone would change what they clicked on the calendar.
 * @param {object} args
 * @param {string} args.timeRange - 'today' | '1d' | 'yesterday' | '3d' | '7d' | '14d' | '30d' | '90d' | '365d' | 'custom'
 * @param {{start?: Date|string, end?: Date|string}} [args.customRange]
 * @param {string} [args.timezone] - IANA timezone name; defaults to UTC
 * @returns {{start_date: string, end_date: string}}
 */
export function getShiftDateRange({ timeRange, customRange, timezone } = {}) {
  const tz = timezone || 'UTC';
  const fmt = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(d);

  if (timeRange === 'custom' && customRange?.start && customRange?.end) {
    const localDay = (value) => {
      const d = new Date(value);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    return { start_date: localDay(customRange.start), end_date: localDay(customRange.end) };
  }

  const now = new Date();
  const DAY_MS = 24 * 60 * 60 * 1000;

  if (timeRange === 'yesterday') {
    const yesterday = fmt(new Date(now.getTime() - DAY_MS));
    return { start_date: yesterday, end_date: yesterday };
  }

  // Offset whole UTC instants then format in the target zone; avoids the DST
  // drift that setDate()-based arithmetic introduces.
  const days = FILTER_RANGE_DAYS[timeRange] || 7;
  return {
    start_date: fmt(new Date(now.getTime() - (days - 1) * DAY_MS)),
    end_date: fmt(now),
  };
}

/**
 * Format time in seconds to human-readable format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time (e.g., "2h 30m", "45m", "5m", "0m")
 */
export function formatTime(seconds) {
  if (!seconds || seconds === 0) return '0m';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    // Less than a minute but more than 0
    return '<1m';
  }
}

/**
 * Convert decimal hours (e.g. 3.52) into an explicit "3h 31m" label so users
 * never misread it as "3h 52m". Returns "Ym" when under an hour, "Xh" when
 * the minute part rounds to 0, and the emptyValue placeholder for null/NaN.
 * This is the canonical hour formatter — prefer it over ad-hoc
 * `.toFixed(1) + 'h'` decimal displays anywhere hours are shown to users.
 * @param {number} hours - decimal hours (e.g. 3.52)
 * @param {{ emptyValue?: string }} [opts]
 * @returns {string} e.g. "3h 31m", "45m", "8h", "—"
 */
export function formatHoursToHM(hours, opts = {}) {
  const { emptyValue = '—' } = opts;
  if (hours == null || isNaN(hours)) return emptyValue;
  const totalMins = Math.round(Math.max(0, hours) * 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h <= 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Round decimal hours to the nearest minute — mirrors the rounding
 * `formatHoursToHM` applies for display (`Math.round(hours * 60)`).
 * Use this before comparing hours against a threshold (e.g. half-day,
 * full-day) so the derived status always agrees with the displayed H:MM
 * figure. Without it, a boundary value like 7.99583h (7h 59m 45s) displays
 * as "8h" but would fail a strict `>= 8` comparison.
 * @param {number} hours - decimal hours (e.g. 7.99583)
 * @returns {number} hours rounded to the nearest minute
 */
export function roundHoursToMinute(hours) {
  if (hours == null || isNaN(hours)) return hours;
  return Math.round(hours * 60) / 60;
}

/**
 * Convert a 24-hour "HH:MM" or "HH:MM:SS" time string to a 12-hour clock
 * label, e.g. "10:00" -> "10:00 AM", "14:05:30" -> "2:05 PM".
 * @param {string} time24
 * @returns {string} formatted label, or '' if input is falsy/unparseable
 */
export function formatTime12h(time24) {
  if (!time24) return '';
  const [hStr, mStr] = String(time24).split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return String(time24);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Duration in whole minutes between two "HH:MM"(:SS) time strings on the
 * same day. Wraps past midnight if end < start (e.g. night-shift ranges).
 * @param {string} startTime24
 * @param {string} endTime24
 * @returns {number|null} minutes, or null if either input is missing/invalid
 */
export function calcDurationMinutes(startTime24, endTime24) {
  if (!startTime24 || !endTime24) return null;
  const toSecs = (t) => {
    const [h, m, s] = String(t).split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 3600 + m * 60 + (s || 0);
  };
  const startSecs = toSecs(startTime24);
  const endSecs = toSecs(endTime24);
  if (startSecs == null || endSecs == null) return null;
  let diff = endSecs - startSecs;
  if (diff < 0) diff += 24 * 3600;
  return Math.round(diff / 60);
}

/**
 * Human-readable duration label from a minute count, e.g.
 * 20 -> "20 minutes", 60 -> "1 hour", 90 -> "1 hour 30 minutes".
 * @param {number} minutes
 * @returns {string}
 */
export function formatDurationLabel(minutes) {
  if (minutes == null || isNaN(minutes)) return '';
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  const hPart = h > 0 ? `${h} hour${h === 1 ? '' : 's'}` : '';
  const mPart = m > 0 ? `${m} minute${m === 1 ? '' : 's'}` : '';
  if (hPart && mPart) return `${hPart} ${mPart}`;
  return hPart || mPart || '0 minutes';
}

/**
 * Combined "10:00 AM – 10:20 AM (20 minutes)" label for a requested time
 * range, used consistently across OD/Swipe request creation, list, approval
 * and dashboard views. Falls back gracefully when a side is missing.
 * @param {string} startTime24
 * @param {string} endTime24
 * @returns {string}
 */
export function formatTimeRangeWithDuration(startTime24, endTime24) {
  if (!startTime24 && !endTime24) return '—';
  if (!startTime24 || !endTime24) return formatTime12h(startTime24 || endTime24) || '—';
  const mins = calcDurationMinutes(startTime24, endTime24);
  const durLabel = mins != null ? ` (${formatDurationLabel(mins)})` : '';
  return `${formatTime12h(startTime24)} – ${formatTime12h(endTime24)}${durLabel}`;
}

/**
 * Productivity category configuration for consistent styling across the app
 */
export const PRODUCTIVITY_CATEGORIES = {
  highly_productive: {
    label: 'Highly Productive',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    bgLight: 'bg-emerald-50',
    icon: ''
  },
  productive: {
    label: 'Productive',
    color: 'bg-green-100 text-green-800 border-green-300',
    bgLight: 'bg-green-50',
    icon: ''
  },
  neutral: {
    label: 'Neutral',
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    bgLight: 'bg-gray-50',
    icon: ''
  },
  unproductive: {
    label: 'Unproductive',
    color: 'bg-red-100 text-red-800 border-red-300',
    bgLight: 'bg-red-50',
    icon: ''
  }
};

/**
 * Get category label from category key
 * @param {string} category - Category key (e.g., "highly_productive")
 * @returns {string} Human-readable label (e.g., "Highly Productive")
 */
export function getCategoryLabel(category) {
  if (!category) return 'Neutral';
  const cat = category.toLowerCase().replace(/\s+/g, '_');
  return PRODUCTIVITY_CATEGORIES[cat]?.label || 'Neutral';
}

/**
 * Get category styling classes
 * @param {string} category - Category key
 * @returns {string} Tailwind CSS classes for the category
 */
export function getCategoryClasses(category) {
  if (!category) return PRODUCTIVITY_CATEGORIES.neutral.color;
  const cat = category.toLowerCase().replace(/\s+/g, '_');
  return PRODUCTIVITY_CATEGORIES[cat]?.color || PRODUCTIVITY_CATEGORIES.neutral.color;
}

/**
 * Get category icon
 * @param {string} category - Category key
 * @returns {string} Category icon emoji
 */
export function getCategoryIcon(category) {
  if (!category) return PRODUCTIVITY_CATEGORIES.neutral.icon;
  const cat = category.toLowerCase().replace(/\s+/g, '_');
  return PRODUCTIVITY_CATEGORIES[cat]?.icon || PRODUCTIVITY_CATEGORIES.neutral.icon;
}
